/**
 * Company › Owned arches (ADR-026): the arches this Company owns.
 *
 *   - Offers waiting for a decision, each naming its arche. Owners review and
 *     decide (OwnedArcheTransferReview); admins see.
 *   - Owned arches: open its management page, its Access panel, its team
 *     (OwnedArcheTeam — offboarding), or release it to a named person
 *     (OwnedArcheReleaseDialog — an owner's act; they must accept).
 *   - Each arche's EARNINGS since it joined the Company (the server bounds the
 *     figures to the current ownership period; shown nowhere else once the
 *     arche is the Company's).
 *   - Releases waiting for their recipient, each naming its arche.
 *
 * Everything here is a Company-management act: session-only, MFA-checked on
 * the server, never reachable with an API key.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import type { CompanyOwnedArcheDto, CompanySettingsDto } from '@/lib/api';
import { formatDollars } from '@/lib/format-credits';
import { ArcheAccessPanel } from '@/contributor/components/ArcheAccessPanel';
import { ARCHE_AUDIENCE, ARCHE_LIFECYCLE_LABEL } from '../../lib/arche-governance-labels';
import { useCancelCompanyArcheRelease, useCompanyArches } from '../../hooks/useArcheGovernance';
import { OwnedArcheReleaseDialog } from './OwnedArcheReleaseDialog';
import { OwnedArcheTeam } from './OwnedArcheTeam';
import { OwnedArcheTransferReview } from './OwnedArcheTransferReview';
import { describeError, personLabel } from './shared';

export function OwnedArchesSection({
  data,
  isOwner,
}: {
  data: CompanySettingsDto;
  isOwner: boolean;
}) {
  const companyId = data.company.companyId;
  const companyName = data.company.name;
  const arches = useCompanyArches(companyId);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [accessFor, setAccessFor] = useState<string | null>(null);
  const [teamFor, setTeamFor] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<CompanyOwnedArcheDto | null>(null);
  const cancelRelease = useCancelCompanyArcheRelease(companyId);

  if (arches.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (arches.isError || !arches.data) {
    return <p className="text-sm text-destructive">{describeError(arches.error)}</p>;
  }
  const owned = arches.data.arches;
  const offers = arches.data.transfers.filter(
    (t) => t.direction === 'to_company' && t.status === 'pending',
  );
  const releases = arches.data.transfers.filter(
    (t) => t.direction === 'release_to_user' && t.status === 'pending',
  );
  // A release is of an arche this Company still owns, so its name is at hand
  // even from a server that does not send one.
  const nameOf = (t: { archeId: string; archeName?: string | null }): string =>
    t.archeName ?? owned.find((a) => a.archeId === t.archeId)?.name ?? 'An arche';

  return (
    <div className="space-y-6">
      {offers.length > 0 && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Offered to {companyName}</CardTitle>
            <CardDescription>
              Someone offered their arche to this Company.{' '}
              {isOwner ? 'Review what accepting means, then decide.' : 'An owner decides.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y rounded-md border">
              {offers.map((t) => (
                <li
                  key={t.transferId}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span>
                    <span className="font-medium">{nameOf(t)}</span>, from{' '}
                    <span className="font-medium">{personLabel(t.proposedBy)}</span>
                    {t.reason && (
                      <span className="block text-xs text-muted-foreground">“{t.reason}”</span>
                    )}
                  </span>
                  {isOwner && (
                    <Button
                      size="sm"
                      aria-label={`Review the offer of ${nameOf(t)}`}
                      onClick={() => setReviewing(t.transferId)}
                    >
                      Review…
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Owned arches</CardTitle>
          <CardDescription>
            Arches this Company owns. Its owners and admins manage them; their earnings go to the
            Company&apos;s payer. Publishers keep publishing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {owned.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This Company owns no arches yet. A creator offers one from the{' '}
              <Link to="/contributor/dashboard" className="text-primary hover:underline">
                Contributor Dashboard
              </Link>
              : open the arche&apos;s Settings, then Ownership → Transfer to a Company.
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {owned.map((a) => {
                const accessId = `owned-arche-access-${a.archeId}`;
                const teamId = `owned-arche-team-${a.archeId}`;
                return (
                  <li key={a.archeId} className="space-y-2 px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{a.name}</span>
                        <Badge variant="outline">{ARCHE_AUDIENCE[a.audience].short}</Badge>
                        {a.siteSnapshotRemovalPending && (
                          <Badge variant="secondary">Finishing restriction…</Badge>
                        )}
                        {a.lifecycleState !== 'active' && (
                          <Badge variant="secondary">
                            {ARCHE_LIFECYCLE_LABEL[a.lifecycleState] ?? a.lifecycleState}
                          </Badge>
                        )}
                        {a.unaffiliatedCount > 0 && (
                          <Badge
                            variant="destructive"
                            title="Collaborators who are no longer on this Company's roster or in a linked workspace"
                          >
                            {a.unaffiliatedCount} outside the Company
                          </Badge>
                        )}
                      </span>
                      <span className="flex gap-2">
                        <Button size="sm" variant="ghost" asChild>
                          <Link to={`/contributor/arches/${encodeURIComponent(a.archeId)}`}>
                            Manage
                          </Link>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={accessFor === a.archeId}
                          aria-controls={accessFor === a.archeId ? accessId : undefined}
                          onClick={() => setAccessFor(accessFor === a.archeId ? null : a.archeId)}
                        >
                          Access
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={teamFor === a.archeId}
                          aria-controls={teamFor === a.archeId ? teamId : undefined}
                          onClick={() => setTeamFor(teamFor === a.archeId ? null : a.archeId)}
                        >
                          Team
                        </Button>
                        {isOwner && (
                          <Button size="sm" variant="ghost" onClick={() => setReleasing(a)}>
                            Release…
                          </Button>
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Earned {formatDollars(a.earnings.totalNetCents)} since it joined {companyName}{' '}
                      · {formatDollars(a.earnings.recentNetCents)} in the last{' '}
                      {a.earnings.recentDays} days.
                    </p>
                    {accessFor === a.archeId && (
                      <div id={accessId}>
                        <ArcheAccessPanel archeId={a.archeId} />
                      </div>
                    )}
                    {teamFor === a.archeId && (
                      <OwnedArcheTeam companyId={companyId} arche={a} id={teamId} />
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {releases.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Being released — waiting for them to accept</h3>
              <ul className="divide-y rounded-md border">
                {releases.map((t) => (
                  <li
                    key={t.transferId}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span>
                      <span className="font-medium">{nameOf(t)}</span>, to{' '}
                      <span className="font-medium">{personLabel(t.target)}</span>
                    </span>
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={cancelRelease.isPending}
                        aria-label={`Withdraw the release of ${nameOf(t)}`}
                        onClick={async () => {
                          try {
                            await cancelRelease.mutateAsync(t.transferId);
                            toast({ title: `Release of ${nameOf(t)} withdrawn` });
                          } catch (err) {
                            toast({
                              title: 'Could not withdraw',
                              description: describeError(err),
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        Withdraw
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </CardContent>
      </Card>

      {reviewing && (
        <OwnedArcheTransferReview
          companyId={companyId}
          companyName={companyName}
          transferId={reviewing}
          onClose={() => setReviewing(null)}
        />
      )}
      {releasing && (
        <OwnedArcheReleaseDialog
          companyId={companyId}
          arche={releasing}
          onClose={() => setReleasing(null)}
        />
      )}
    </div>
  );
}
